import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import clsx from "classnames";
import ButtonSmg from "../ButtonSmg";
import { WrappedLinkRouter } from "../Link";

const useStyles = makeStyles(theme => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    position: "relative",
    boxSizing: "border-box",
    borderBottom: "1px solid transparent",
    paddingBottom: 1,
  },
  rootActive: {
    borderBottomColor: "#e17300",
  },
  button: {
    fontFamily: '"Instrument Sans", Helvetica, Arial, sans-serif',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.25,
    color: "#6b6b6b",
    textTransform: "none",
    border: 0,
    borderRadius: 0,
    minWidth: 0,
    minHeight: 0,
    margin: 0,
    padding: 0,
    whiteSpace: "nowrap",
    "&&": {
      minWidth: 0,
      margin: 0,
      padding: 0,
      color: "#6b6b6b",
    },
    "&:focus": {
      backgroundColor: "transparent",
    },
    "&:hover": {
      backgroundColor: "transparent",
      color: "#6b6b6b",
    },
  },
}));

const pathMatches = (pathname, to) => {
  if (!to || !pathname) return false;
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
};

/** Prefijos por sección (ítems con dropdown no tienen `to` propio). */
const SECTION_PREFIXES = {
  empresas: ["/empresas"],
  institucional: ["/nuestra-empresa", "/sucursales"],
  familias: ["/familias"],
};

export const isMenuItemActive = (item, pathname) => {
  const prefixes = SECTION_PREFIXES[item.key];
  if (prefixes && prefixes.some(prefix => pathMatches(pathname, prefix))) {
    return true;
  }
  if (item.elemens && item.elemens.length) {
    return item.elemens.some(el => pathMatches(pathname, el.to));
  }
  return pathMatches(pathname, item.to);
};

/**
 * @param {object} props
 * @param {object} props.item
 * @param {string|null} props.openMenuKey
 * @param {string|null} props.highlightMenuKey
 * @param {(key: string|null) => void} props.onOpenMenu
 * @param {(key: string) => void} props.onSelectNav
 */
export default function HeaderMenu({
  item,
  openMenuKey,
  highlightMenuKey,
  onOpenMenu,
  onSelectNav,
}) {
  const classes = useStyles();
  const { pathname } = useLocation();
  const routeActive = useMemo(() => isMenuItemActive(item, pathname), [item, pathname]);
  const menuOpen = Boolean(item.static && openMenuKey === item.key);
  const showUnderline = openMenuKey
    ? openMenuKey === item.key
    : highlightMenuKey
      ? highlightMenuKey === item.key
      : routeActive;

  const handleClick = useCallback(
    event => {
      if (!item.static) {
        onSelectNav(item.key);
        return;
      }
      event.stopPropagation();
      onOpenMenu(openMenuKey === item.key ? null : item.key);
    },
    [item.static, item.key, openMenuKey, onOpenMenu, onSelectNav]
  );

  /* Cerrar al click afuera (el panel full-width también usa data-header-menu). */
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onDocMouseDown = event => {
      if (event.target.closest(`[data-header-menu="${item.key}"]`)) return;
      onOpenMenu(null);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [menuOpen, onOpenMenu, item.key]);

  return (
    <div
      data-header-menu={item.key}
      className={clsx(classes.root, showUnderline && classes.rootActive)}
    >
      <ButtonSmg
        id={`btn-header-${item.key}`}
        aria-controls={item.static ? `mega-${item.key}` : undefined}
        aria-haspopup={item.static ? "true" : undefined}
        aria-expanded={menuOpen ? "true" : undefined}
        aria-current={routeActive ? "page" : undefined}
        variant="text"
        color="inherit"
        onClick={handleClick}
        size="large"
        buttonRoot={false}
        className={classes.button}
        content={item.label}
        {...(!item.static
          ? {
              component: WrappedLinkRouter,
              to: item.to || `/${item.key}`,
            }
          : {})}
      />
    </div>
  );
}
